import React from 'react';
import { useTranslation } from 'react-i18next';
import TableCmp from 'src/components/TableCmp';
import { getFormattedNumber } from 'src/utils/CommonFunctions';

interface IDimension {
    handlingUnit?: any;
    handlingUnitNo?: number;
    length?: number;
    width?: number;
    height?: number;
    totalWeight?: number;
    weightMeasurement?: string;
    freightClass?: string;
    [key: string]: any;
}

interface ILineItemsTooltipContentProps {
    dimensions?: IDimension[];
    weightMeasurement?: string;
    handlingUnitOptions?: { value: number; label: string }[];
    totalWeightDisplay?: string;
    totalLineItems?: any;
    totalLinearFt?: any;
}

const LineItemDetailRow = ({ data, weightMeasurement, handlingUnitOptions }: { data: IDimension; weightMeasurement: string; handlingUnitOptions?: { value: number; label: string }[] }) => {
    const handlingUnitLabel = handlingUnitOptions?.find((o) => Number(o.value) === Number(data?.handlingUnit))?.label;

    return (
        <>
            <td className="w-[24%]">{handlingUnitLabel || data?.handlingUnit || '-'}</td>
            <td className="w-[14%]">{data?.handlingUnitNo || 1}</td>
            <td className="w-[25%]">{data?.length && data?.width && data?.height ? `${data.length}"x${data.width}"x${data.height}"` : '-'}</td>
            <td className="w-[16%]">{data?.freightClass || '-'}</td>
            <td className="w-[21%] text-right">{data?.totalWeight ? `${getFormattedNumber(data.totalWeight, false)} ${weightMeasurement}` : '-'}</td>
        </>
    );
};

const LineItemsTotalsRow = ({ totalLineItems, totalLinearFt, totalWeightDisplay }: { totalLineItems?: any; totalLinearFt?: any; totalWeightDisplay?: string }) => {
    const { t } = useTranslation();

    return (
        <>
            <td className="w-[33.33%] text-grayLight900 font-medium">
                <div className="h-full flex items-center gap-x-2 justify-between">
                    <p>{t('Common.Line_Item')}</p>
                    <p>{totalLineItems}</p>
                </div>
            </td>
            <td className="w-[33.33%] text-grayLight900 font-medium">
                <div className="h-full flex items-center gap-x-2 justify-between">
                    <p>{t('Common.Total_Linear_Ft')}</p>
                    <p>{totalLinearFt}</p>
                </div>
            </td>
            <td className="w-[33.33%] text-grayLight900 font-medium">
                <div className="h-full flex items-center gap-x-2 justify-between">
                    <p>{t('Common.Total_Weight')}</p>
                    <p>{totalWeightDisplay}</p>
                </div>
            </td>
        </>
    );
};

const LineItemsFooterRow = ({ totalLineItems, totalLinearFt, totalWeightDisplay }: { totalLineItems?: any; totalLinearFt?: any; totalWeightDisplay?: string }) => (
    <td colSpan={5} className="!p-0">
        <TableCmp
            tableRowHeight="xs"
            tableDataArr={[{}]}
            tableBodyRowClass="divide-x divide-utilityGray200"
            TableRowCmp={LineItemsTotalsRow}
            tableRowCmpProps={{ totalLineItems, totalLinearFt, totalWeightDisplay }}
        />
    </td>
);

/**
 * "Line Items" card behind the Route Overview Description cell — port of WALHQ's
 * `V3DropdownCmp/LineItemsTooltipContent` so the portal's order detail reads like WAL's:
 * one row per dimension line (handling unit, count, LxWxH, freight class, weight) plus a totals footer.
 */
const LineItemsTooltipContent = ({ dimensions = [], weightMeasurement = 'lbs', handlingUnitOptions, totalWeightDisplay = '-', totalLineItems, totalLinearFt }: ILineItemsTooltipContentProps) => {
    const { t } = useTranslation();

    return (
        <>
            <p className="text-xs text-grayLight900 font-medium">{t('Common.Line_Items')}</p>
            <div className="tooltip-content-with-scroll pb-1">
                <div className="table-box-wrap">
                    <div className="table-box-container overflow-hidden">
                        <div className="overflow-x-auto custom-scrollbar scrollbar-hide table-without-title-header table-without-pagination">
                            <TableCmp
                                headCells={[
                                    { id: 'handlingUnitName', name: t('Common.Handling_Unit'), visible: true },
                                    { id: 'handlingUnitNo', name: '№', visible: true },
                                    { id: 'dimensions', name: t('Common.Dimensions'), visible: true },
                                    { id: 'freightClass', name: 'FC', visible: true },
                                    { id: 'totalWeight', name: t('Common.Weight'), visible: true, headCellsTextClass: 'justify-end' },
                                ]}
                                tableDataArr={dimensions}
                                TableRowCmp={LineItemDetailRow}
                                tableRowCmpProps={{
                                    weightMeasurement: (dimensions?.[0]?.weightMeasurement || weightMeasurement || 'lbs').toLowerCase(),
                                    handlingUnitOptions,
                                }}
                                tableRowHeight="xs"
                                footerDataArr={[{}]}
                                tableBodyRowClass="last:!border-utilityGray200"
                                tableFooterRowClass="!border-b-0"
                                TableFooterRowCmp={LineItemsFooterRow}
                                tableFooterRowCmpProps={{ totalLineItems, totalLinearFt, totalWeightDisplay }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LineItemsTooltipContent;
